/**
 * The operator's stated reason for observability queries (spec §122).
 *
 * The Observability API audits every query and refuses those that name business
 * objects (domains, emails, IDs, long numbers) without an `X-Ops-Reason` header
 * (`REASON_REQUIRED`). The UI asks once, keeps the reason in memory for this tab,
 * and sends it with every `/ops/v1` request until the operator clears or changes it.
 * It is never persisted.
 */

import { useSyncExternalStore } from 'react';

export const MIN_REASON_LENGTH = 5;
export const MAX_REASON_LENGTH = 300;

let reason: string | null = null;
const listeners = new Set<() => void>();

/**
 * HTTP header values must be visible ASCII. Diacritics are folded ("Kiểm tra" ->
 * "Kiem tra"), other characters become "?", whitespace is collapsed.
 */
export function toHeaderSafe(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .replace(/\s+/g, ' ')
    .replace(/[^\x20-\x7e]/g, '?')
    .trim()
    .slice(0, MAX_REASON_LENGTH);
}

export function getOpsReason(): string | null {
  return reason;
}

export function setOpsReason(value: string | null): void {
  const next = value ? toHeaderSafe(value) : null;
  reason = next && next.length >= MIN_REASON_LENGTH ? next : null;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useOpsReason(): string | null {
  return useSyncExternalStore(subscribe, getOpsReason, getOpsReason);
}
